import { ChartInfoKind } from '@frontend/chart/types';
import { EntityType, formatTimeDifference } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage, useIntl } from 'react-intl';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { InsightId } from '../insight-registry/insight-ids';
import { EquipmentUsageHoursItem, useLendingEquipmentUsageHoursList } from './use-lending-equipment-usage-hours-list';

export interface LendingEquipmentUsageHoursListProps {
    accountId: string;
    width?: InsightCardWidth;
}

const formatDuration = (durationSeconds: number): string => {
    const end = Date.now();
    return formatTimeDifference(new Date(end - durationSeconds * 1000).toISOString(), new Date(end).toISOString());
};

const ProductCell = ({ item }: { item: EquipmentUsageHoursItem }) => {
    if (item.productName && item.productEntityId && item.productEntityType) {
        return (
            <PlatformObjectBadge
                display={item.productName}
                entityType={item.productEntityType}
                id={item.productEntityId}
            />
        );
    }

    if (item.showEquipmentBadge) {
        return (
            <PlatformObjectBadge
                display={item.equipmentId}
                entityType={EntityType.EQUIPMENT}
                id={item.equipmentId}
            />
        );
    }

    return <span className='text-foreground-muted'>—</span>;
};

const SerialCell = ({ item }: { item: EquipmentUsageHoursItem }) => {
    const parts = [item.serialNumber, item.tagName].filter(Boolean);
    if (parts.length === 0) {
        return <span className='text-foreground-muted'>—</span>;
    }

    return <span className='truncate'>{parts.join(' · ')}</span>;
};

export const LendingEquipmentUsageHoursList = ({ accountId, width }: LendingEquipmentUsageHoursListProps) => {
    const { formatMessage } = useIntl();
    const { items, isLoading, hasData } = useLendingEquipmentUsageHoursList(accountId);

    const durationHeader = formatMessage({
        id: 'platform.analytics.insights.lending-equipment-usage-hours-list.duration-column',
        description: 'Column header for total time in use on the lending asset usage hours list',
        defaultMessage: 'Duration'
    });

    return (
        <InsightCard
            insightId={InsightId.LENDING_ASSET_USAGE_HOURS}
            infoKind={ChartInfoKind.EQUIPMENT_DURATION_BY_EQUIPMENT_LIST}
            width={width}
            title={
                <FormattedMessage
                    id='platform.analytics.insights.lending-equipment-usage-hours-list.title'
                    description='Title for the lending per-asset usage hours list on analytics insights'
                    defaultMessage='Asset usage hours'
                />
            }
            subtitle={
                <FormattedMessage
                    id='platform.analytics.insights.lending-equipment-usage-hours-list.subtitle'
                    description='Subtitle indicating asset usage hours covers the last 30 days for individual equipment'
                    defaultMessage='last 30 days · by equipment'
                />
            }
            isLoading={isLoading}>
            {!hasData ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : (
                <div className='min-h-0 w-full flex-1 overflow-y-auto'>
                    <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                        <div className='w-full min-w-0 px-4 py-3'>{CommonMessage.OBJECTS.PRODUCT.DEFAULT}</div>
                        <div className='w-[140px] shrink-0 px-4 py-3'>{CommonMessage.OBJECTS.EQUIPMENT.SERIAL_NUMBER}</div>
                        <div className='w-[120px] shrink-0 px-4 py-3 text-right'>{durationHeader}</div>
                    </div>
                    {items.map((item) => (
                        <div
                            key={item.key}
                            className='border-border-default flex h-11 items-center gap-4 border-b'>
                            <div className='text-foreground-default flex w-full min-w-0 items-center px-4 text-sm font-medium'>
                                <ProductCell item={item} />
                            </div>
                            <div className='text-foreground-default flex w-[140px] shrink-0 items-center px-4 text-sm font-medium'>
                                <SerialCell item={item} />
                            </div>
                            <div className='flex w-[120px] shrink-0 justify-end pe-4'>
                                <span className='text-background-surface bg-brand-muted inline-flex shrink-0 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold'>
                                    {formatDuration(item.durationSeconds)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </InsightCard>
    );
};
